import React, { Component } from 'react';
import GoogleMap from 'google-map-react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import config from '../../config';

const googleMapsApiKey = () => {
	const apiKey = config.REACT_APP_GOOGLE_MAPS_API_KEY || '';
	return apiKey;
};

const bootstrapURLKeys = {
  key: googleMapsApiKey(),
  libraries: ['drawing', 'places'].join(','),
}

// Map helper functions

let overlaySortFunction = (a, b) => {
  if (a._id === b._id) return 0;
  if (a._id < b._id) return -1;
  return 1;
};

const diffOverlays = (oldOverlays, nextOverlays) => {
  let removeOverlayIds = [];
  let addOverlayIds = [];

  const nextOverlaysSorted = [...nextOverlays];
  const oldOverlaysSorted = [...oldOverlays];

  nextOverlaysSorted.sort(overlaySortFunction);
  oldOverlaysSorted.sort(overlaySortFunction);

  let i = 0;
  let j = 0;

  while (i < nextOverlaysSorted.length || j < oldOverlaysSorted.length) {
    if (j >= oldOverlaysSorted.length) {
      // keep adding new ids
      addOverlayIds.push(nextOverlaysSorted[i]);
      i += 1;
      continue;
    }
    if (i >= nextOverlaysSorted.length) {
      // keep remove old obsolete ids
      removeOverlayIds.push(oldOverlaysSorted[j]);
      j += 1;
      continue;
    }

    if (nextOverlaysSorted[i]._id === oldOverlaysSorted[j]._id) {
      i += 1;
      j += 1;
      continue;
    }
    if (nextOverlaysSorted[i]._id < oldOverlaysSorted[j]._id) {
      addOverlayIds.push(nextOverlaysSorted[i]);
      i += 1;
      continue;
    }
    if (nextOverlaysSorted[i]._id > oldOverlaysSorted[j]._id) {
      removeOverlayIds.push(oldOverlaysSorted[j]);
      j += 1;
      continue;
    }
  }
  return {
    removeOverlayIds,
    addOverlayIds,
  }
}

function getGeoJSONPolygonFromOverlay(overlay){
  const geoJSON = {
    type: 'Polygon',
    coordinates: [],
  }
  const rawCoordinates = overlay.getPath().getArray();
  const coordinates = [];
  for (let i = 0; i < rawCoordinates.length; i += 1) {
    coordinates.push([
      rawCoordinates[i].lng(),
      rawCoordinates[i].lat(),
    ]);
  }
  if (coordinates.length > 0) coordinates.push(coordinates[0]);
  
  geoJSON.coordinates.push(coordinates);
  return geoJSON;
}

function createMapOptions(maps) {
  return {
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_CENTER,
      style: maps.ZoomControlStyle.SMALL
    },
    mapTypeControlOptions: {
      position: maps.ControlPosition.TOP_RIGHT,
    },
    streetViewControl: true,
    streetViewControlOptions: {
        position: maps.ControlPosition.RIGHT_BOTTOM,
    },
    mapTypeControl: true,
    mapTypeId: 'hybrid',
    styles: [
      {
          featureType: "poi",
          elementType: "labels",
          stylers: [
                { visibility: "on" },
          ],
      },
    ],
  };
}

export default class Map extends Component {
  constructor(props) {
    super(props);
    this.google = null;
    this.drawingManager = null;
    this.state = {
      googleMapPolygons: {},
      googleMapCircles: {},
      googleMapMarkers: {},
      
      googleMapFences: {},
      googleMapPois: {},
      newlyDrawnFences: [],
      infoWindow: null,
    }
  }

  generateGoogleMarker(marker){
    const newMarker = new this.google.maps.Marker({
      position: {
        lng: marker.geo.coordinates[0],
        lat: marker.geo.coordinates[1],
      },
      zIndex: 9999,
      draggable: false,
      title: marker.htmlContent,
    });
    newMarker.addListener('click', () => {
      this.state.infoWindow.setContent(newMarker.title);
      this.state.infoWindow.open(this.map, newMarker);
    });
    return newMarker;
  }

  generateGoogleFence(fence, editMode){
    if (fence.geo.type === 'Point') {
        const newCircle = new this.google.maps.Circle({
          strokeColor: '#4A4A4A',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: fence.color || '#6DDCED',
          fillOpacity: 0.6,
          center: {
            lng: fence.geo.coordinates[0],
            lat: fence.geo.coordinates[1],
          },
          radius: fence.radius || 50,
          editable: false,
          draggable: false,
        });
        return newCircle;
      }
      if (fence.geo.type === 'Polygon') {
        const path = fence.geo.coordinates[0].map((c) => {
          return {
            lng: c[0],
            lat: c[1],
          }
        });

        var newPolygon = new this.google.maps.Polygon({
          paths: path,
          strokeColor: fence.accuracy === 1 ? '#4A4A4A': '#4A4A4A',
          strokeOpacity: 0.6,
          strokeWeight: 1,
          fillColor: fence.color || '#6DDCED',
          fillOpacity: 0.6,
          title: "Test",
          editable: false,
          draggable: false,
        });
        
        return newPolygon;
      }
      return null;
  }

  drawOverlays(overlayDiff, editMode) {
    let newGoogleMapPolygons = { ...this.state.googleMapPolygons };

    // DRAW FENCES RETRIEVED FROM THE SERVER
    for (let cur of overlayDiff.addOverlayIds) {
      var newPolygon = this.generateGoogleFence(cur, editMode);
      newPolygon.setMap(this.google.map);
      newGoogleMapPolygons[cur._id] = newPolygon;
    }

    // REMOVE OLD FENCES
    for (let cur of overlayDiff.removeOverlayIds) {
      if (!_.isUndefined(newGoogleMapPolygons[cur._id]) && _.isFunction(newGoogleMapPolygons[cur._id].setMap)) {
        newGoogleMapPolygons[cur._id].setMap(null);
        delete newGoogleMapPolygons[cur._id];
      }
    }

    this.setState({
      googleMapPolygons: newGoogleMapPolygons,
    });
  }

  drawPois(markersDiff){
    let newGoogleMarkers = { ...this.state.googleMapMarkers};
    
    // DRAW FENCES RETRIEVED FROM THE SERVER
    for (let cur of markersDiff.addOverlayIds) {
      switch (cur.geo.type) {
        case 'Point':
          var newMarker = this.generateGoogleMarker(cur);
          newMarker.setMap(this.google.map);
          newGoogleMarkers[cur._id] = newMarker;
          break;
        default:
          continue;
      }
    }

    for (let cur of markersDiff.removeOverlayIds) {
      if (!_.isUndefined(newGoogleMarkers[cur._id]) && _.isFunction(newGoogleMarkers[cur._id].setMap)) {
        newGoogleMarkers[cur._id].setMap(null);
        delete newGoogleMarkers[cur._id];
      }
    }

    this.setState({
      googleMapMarkers: newGoogleMarkers,
    });
  }

  componentWillUnmount() {
    if (_.isFunction(this.props.onWillUnmount)) {
      this.props.onWillUnmount();
    }
  }

  handleUpdateInput(value){
    if (_.isFunction(this.props.updateSearchTerm)) {
      this.props.updateSearchTerm(value);
    }
  }

  setMapBounds(data){
    if (_.isFunction(this.props.setMapBounds)){
      this.props.setMapBounds(data);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (_.isUndefined(this.google) || _.isNull(this.google)
        || _.isUndefined(this.google.maps) || _.isNull(this.google.maps)) return;

    let nextPolygonsSorted = nextProps.polygons;
    let oldPolygonsSorted = this.props.polygons;

    let nextCirclesSorted = [];
    let oldCirclesSorted = [];

    if (!_.isUndefined(nextProps.circles)){
      nextCirclesSorted = nextProps.circles;
    }
    
    if (!_.isUndefined(this.props.circles)){
      oldCirclesSorted = this.props.circles;
    }
    
    let nextMarkersSorted = nextProps.markers;
    let oldMarkersSorted = this.props.markers;

    const polygonDiff = diffOverlays(oldPolygonsSorted, nextPolygonsSorted);
    const circleDiff = diffOverlays(oldCirclesSorted, nextCirclesSorted);

    const markersDiff = diffOverlays(oldMarkersSorted, nextMarkersSorted);

    if (polygonDiff.addOverlayIds.length > 0 || polygonDiff.removeOverlayIds.length > 0
       || circleDiff.addOverlayIds.length > 0 || circleDiff.removeOverlayIds.length > 0) {
         for (let i = 0; i < this.state.newlyDrawnFences.length; i += 1) {
           this.state.newlyDrawnFences[i].setMap(null);
         }
         this.setState({
            newlyDrawnFences: [],
         });
    }

    this.drawOverlays(polygonDiff, nextProps.editMode);
    this.drawPois(markersDiff);
    this.google.map.setTilt(0);

    // Handle Toggling Draw Mode
    if (nextProps.drawMode) {
      this.drawingManager.setMap(this.google.map);
    } else {
      this.drawingManager.setMap(null);
    }

    // Set map cetner

    if (nextProps.mapCenter !== null) {
      this.google.map.setCenter(nextProps.mapCenter);
      this.google.map.setZoom(18);
    }
    
    // Set Map bounds

    if (!_.isUndefined(nextProps.mapBounds) 
        && !_.isNull(nextProps.mapBounds) 
        && !_.isUndefined(nextProps.mapBounds.southwest) && !_.isUndefined(nextProps.mapBounds.northeast)) {
      const newSW = new this.google.maps.LatLng(nextProps.mapBounds.southwest);
      const newNE = new this.google.maps.LatLng(nextProps.mapBounds.northeast);
      const newBounds = new this.google.maps.LatLngBounds(newSW, newNE);
      
      this.google.map.setTilt(0);
      this.google.map.setZoom(18);
      this.google.map.panToBounds(newBounds);
    }
  }

  handleGoogleMapApi(google) {

    this.google = google;
    const map = google.map
    const self = this;

    if (_.isNull(map)) return;

    // Disable tilt on zoom
    map.setTilt(0);

    // Setup Drawing Manager
    const drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      drawingControl: false,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
        drawingModes: [],
      },
      circleOptions: {
        fillColor: '#50E3C2',
        fillOpacity: 0.8,
        strokeWeight: 3,
        clickable: false,
        editable: false,
        zIndex: 1
      },
      polygonOptions: {
        fillColor: '#FF0221',
        editable: this.props.editMode,
      },
    });

    this.drawingManager = drawingManager;

    if (self.props.drawMode) {
      drawingManager.setMap(map);
    }

    // Drawing Manager callbacks

    google.maps.event.addListener(drawingManager, 'overlaycomplete', function (event) {
      let geoJSON = null;
      if (event.type === 'polygon') {
        geoJSON = getGeoJSONPolygonFromOverlay(event.overlay);
      }
      if (event.type === 'circle') {
        geoJSON = {
          type: 'Point',
          coordinates: [
            event.overlay.center.lng(),
            event.overlay.center.lat(),
          ]
        }
      }
      self.drawingManager = drawingManager;

      event.overlay.setMap(null);

      if (_.isFunction(self.props.newFenceDrawn)) {
        self.props.newFenceDrawn({ geometry: geoJSON });
      }

      self.setState({
        newlyDrawnFences: [...self.state.newlyDrawnFences, event.overlay],
      });
    });

    // Info window

    var infoWindow = new google.maps.InfoWindow({
      content: '',
      maxWidth: 300,
    });

    this.setState({
      infoWindow,
    });

    // If the center is set, set map center

    if (!_.isNull(this.props.mapCenter) || !_.isUndefined(this.props.mapCenter)) {
      if (_.isFunction(map.setCenter)) map.setCenter(this.props.mapCenter);
      if (_.isFunction(map.setZoom)) map.setZoom(20);
    }

    // If the map bounds are set, set map bounds

    if (!_.isUndefined(this.props.mapBounds) 
        && !_.isNull(this.props.mapBounds) 
        && !_.isUndefined(this.props.mapBounds.southwest) && !_.isUndefined(this.props.mapBounds.northeast)) {
      const newSW = new google.maps.LatLng(this.props.mapBounds.southwest);
      const newNE = new google.maps.LatLng(this.props.mapBounds.northeast);
      const newBounds = new google.maps.LatLngBounds(newSW, newNE);

      if (_.isFunction(map.setZoom)) map.setZoom(18);
      if (_.isFunction(map.setMapBounds)) map.setMapBounds(newBounds);
    }

    // Draw fences and POIs
    if (!_.isUndefined(this.google) && !_.isUndefined(this.google.maps)) {
      let polygonsSorted = this.props.polygons;
      let circlesSorted = this.props.circles;
      let markersSorted = this.props.markers;

      const polygonDiff = diffOverlays([], polygonsSorted);
      const circleDiff = diffOverlays([], circlesSorted);
      const markersDiff = diffOverlays([], markersSorted);

      this.drawOverlays(polygonDiff, self.props.editMode);
      this.drawPois(markersDiff);
    }

    // Set Event Listeners

    map.addListener('center_changed', () => {
      if (_.isFunction(self.props.centerChanged)) self.props.centerChanged(null);
      if (_.isFunction(self.props.setMapBounds)) self.props.setMapBounds(null);
    });
    map.addListener('bounds_changed', () => {
      try{
        if (_.isFunction(map.getBounds) && map.getBounds() 
            && _.isFunction(map.getBounds().toJSON)) {

          const bounds = map.getBounds().toJSON();
          const boundsFormatted = {
            topLeft: [bounds.west, bounds.north],
            bottomRight: [bounds.east, bounds.south],
          };
          
          if (_.isFunction(this.props.viewpointChanged)) {
            this.props.viewpointChanged(boundsFormatted.topLeft, boundsFormatted.bottomRight);
          }
        }
      } catch(e){
        console.log(e);
      }
      
    });
  }

  render() {
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative'}}>
        <GoogleMap
          center={{ lat: -34.397, lng: 150.644 }}
          defaultZoom={8}
          bootstrapURLKeys={bootstrapURLKeys}
          options={createMapOptions}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={this.handleGoogleMapApi.bind(this)}>
        </GoogleMap>
      </div>
    );
  }
}
//React.PropTypes.func,

Map.propTypes = {
  drawMode: PropTypes.bool,
  editMode: PropTypes.bool,

  circles: PropTypes.array,
  polygons: PropTypes.array,
  markers: PropTypes.array,
  
  drawOnlyTopFence: PropTypes.bool,
  draggableMarkers: PropTypes.bool,
  showGeocode: PropTypes.bool,
  
  mapBounds: PropTypes.shape({
    southwest: PropTypes.object,
    northeast: PropTypes.object,
  }),

  mapCenter: PropTypes.object,

  viewpointChanged: PropTypes.func,
  setMapBounds: PropTypes.func,
  onWillUnmount: PropTypes.func,
  updateSearchTerm: PropTypes.func,
  onFenceUpdate: PropTypes.func,
  onMarkerChange: PropTypes.func,
  newFenceDrawn: PropTypes.func,
}

Map.defaultProps = {
  drawMode: true,
  editMode: false,
  showGeocode: true,

  circles: [],
  polygons: [],
  markers: [],

  drawOnlyTopFence: true,
  draggableMarkers: false,
}



// WEBPACK FOOTER //
// ./src/shared/components/Map.js